import { Fragment, useEffect, useState } from 'react'
import { api } from '../api/client'

interface ParameterAntwort {
  id: number
  schluessel: string
  wert: string | null
  typ: string
  gruppe: string | null
  bezeichnung: string | null
  beschreibung: string | null
  standardwert: string | null
}

/**
 * Bildet einen BCrypt-Hash für einen anderen Rechner, etwa die Entwicklungsumgebung.
 *
 * Gespeichert wird hier nichts: „Schlüssel erzeugen“ unter Posteingang schriebe den Hash in
 * dieses Portal und sperrte den laufenden Hausdienst aus. Übernehmen setzt nur den Entwurf
 * im Feld — gespeichert wird erst mit „Speichern“, wie bei jeder anderen Änderung.
 */
function HashBilden({ onUebernehmen }: { onUebernehmen: (hash: string) => void }) {
  const [offen, setOffen] = useState(false)
  const [eingabe, setEingabe] = useState('')
  const [ergebnis, setErgebnis] = useState<{ klartext: string; hash: string } | null>(null)
  const [kopiert, setKopiert] = useState<string | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  async function bilden() {
    setFehler(null)
    setKopiert(null)
    try {
      setErgebnis(
        await api.post<{ klartext: string; hash: string }>('/api/plattform/parameter/hash', {
          klartext: eingabe,
        }),
      )
    } catch (ex) {
      setFehler(ex instanceof Error ? ex.message : 'Fehlgeschlagen')
    }
  }

  async function kopieren(was: string, text: string) {
    try {
      await navigator.clipboard.writeText(text)
      setKopiert(was)
    } catch {
      // Ohne HTTPS gibt es keine Zwischenablage — das Feld lässt sich per Doppelklick markieren.
      setFehler('Kopieren nicht möglich — bitte den Text markieren und selbst kopieren.')
    }
  }

  if (!offen) {
    return (
      <button
        className="knopf-schlicht"
        style={{ padding: '4px 10px', marginTop: 6, fontSize: '0.8rem' }}
        onClick={() => setOffen(true)}
      >
        BCrypt-Hash erzeugen …
      </button>
    )
  }

  return (
    <div className="meldung hinweis" style={{ marginTop: 8 }}>
      <div style={{ fontSize: '0.84rem', marginBottom: 6 }}>
        Für ein anderes Portal: Den Hash dort unter Parameter eintragen, den Klartext dem
        Hausdienst geben. <strong>Hier wird nichts gespeichert.</strong>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          type="text"
          placeholder="Klartext — leer lassen für einen zufälligen"
          value={eingabe}
          onChange={(e) => setEingabe(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && void bilden()}
          style={{ flex: 1 }}
        />
        <button className="knopf-primaer" style={{ padding: '6px 14px' }} onClick={() => void bilden()}>
          Hash bilden
        </button>
      </div>
      {fehler && <div style={{ color: 'var(--rot-500)', marginTop: 6 }}>{fehler}</div>}
      {ergebnis && (
        <div style={{ marginTop: 8, fontSize: '0.84rem' }}>
          <div>Klartext (für den Hausdienst)</div>
          <pre className="schluessel-anzeige">{ergebnis.klartext}</pre>
          <button
            className="knopf-schlicht"
            style={{ padding: '4px 10px', marginTop: 4 }}
            onClick={() => void kopieren('klartext', ergebnis.klartext)}
          >
            {kopiert === 'klartext' ? 'Kopiert ✓' : 'Klartext kopieren'}
          </button>
          <div style={{ marginTop: 10 }}>BCrypt-Hash (für den Parameter im anderen Portal)</div>
          <pre className="schluessel-anzeige">{ergebnis.hash}</pre>
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button
              className="knopf-schlicht"
              style={{ padding: '4px 10px' }}
              onClick={() => void kopieren('hash', ergebnis.hash)}
            >
              {kopiert === 'hash' ? 'Kopiert ✓' : 'Hash kopieren'}
            </button>
            <button
              className="knopf-schlicht"
              style={{ padding: '4px 10px' }}
              onClick={() => onUebernehmen(ergebnis.hash)}
            >
              In dieses Feld übernehmen
            </button>
          </div>
        </div>
      )}
      <button
        className="knopf-schlicht"
        style={{ padding: '4px 10px', marginTop: 10, fontSize: '0.8rem' }}
        onClick={() => {
          setOffen(false)
          setErgebnis(null)
          setEingabe('')
          setFehler(null)
        }}
      >
        Schließen
      </button>
    </div>
  )
}

export function Parameter() {
  const [parameter, setParameter] = useState<ParameterAntwort[]>([])
  const [entwuerfe, setEntwuerfe] = useState<Record<number, string>>({})
  const [laedt, setLaedt] = useState(true)
  const [meldung, setMeldung] = useState<{ art: string; text: string } | null>(null)

  async function laden() {
    setLaedt(true)
    try {
      const daten = await api.get<ParameterAntwort[]>('/api/plattform/parameter')
      setParameter(daten)
      setEntwuerfe(Object.fromEntries(daten.map((p) => [p.id, p.wert ?? ''])))
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Laden fehlgeschlagen' })
    } finally {
      setLaedt(false)
    }
  }

  useEffect(() => {
    void laden()
  }, [])

  async function speichern(p: ParameterAntwort) {
    try {
      await api.put(`/api/plattform/parameter/${p.id}`, { wert: entwuerfe[p.id] })
      setMeldung({ art: 'erfolg', text: `„${p.bezeichnung}" gespeichert` })
      setParameter((alt) =>
        alt.map((x) => (x.id === p.id ? { ...x, wert: entwuerfe[p.id] } : x)),
      )
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Speichern fehlgeschlagen' })
    }
  }

  const gruppen = [...new Set(parameter.map((p) => p.gruppe ?? 'Sonstige'))]

  return (
    <>
      <div className="inhalt-kopf">
        <div>
          <h1>Parameter</h1>
          <p>Plattformweite Einstellungen. Sie gelten für alle Mandanten.</p>
        </div>
      </div>

      {meldung && <div className={`meldung ${meldung.art}`}>{meldung.text}</div>}

      {laedt ? (
        <div className="laden">Wird geladen …</div>
      ) : (
        gruppen.map((gruppe) => (
          <div className="abschnitt" key={gruppe}>
            <h2>{gruppe}</h2>
            <div className="tabelle-huelle">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: '32%' }}>Einstellung</th>
                    <th>Wert</th>
                    <th style={{ width: 130 }} />
                  </tr>
                </thead>
                <tbody>
                  {parameter
                    .filter((p) => (p.gruppe ?? 'Sonstige') === gruppe)
                    .map((p) => {
                      const geaendert = (entwuerfe[p.id] ?? '') !== (p.wert ?? '')
                      return (
                        <Fragment key={p.id}>
                          <tr>
                            <td>
                              <strong>{p.bezeichnung ?? p.schluessel}</strong>
                              <div style={{ color: 'var(--grau-500)', fontSize: '0.8rem', marginTop: 3 }}>
                                {p.beschreibung}
                              </div>
                              <div
                                style={{
                                  color: 'var(--grau-300)',
                                  fontSize: '0.74rem',
                                  fontFamily: 'var(--schrift-mono)',
                                  marginTop: 3,
                                }}
                              >
                                {p.schluessel}
                              </div>
                            </td>
                            <td>
                              {p.typ === 'MEHRZEILIG' ? (
                                <textarea
                                  rows={3}
                                  style={{
                                    width: '100%',
                                    padding: '9px 11px',
                                    border: '1px solid var(--grau-300)',
                                    borderRadius: 'var(--radius-klein)',
                                    fontFamily: 'inherit',
                                    fontSize: '0.92rem',
                                  }}
                                  value={entwuerfe[p.id] ?? ''}
                                  onChange={(e) =>
                                    setEntwuerfe({ ...entwuerfe, [p.id]: e.target.value })
                                  }
                                />
                              ) : p.typ === 'JA_NEIN' ? (
                                <select
                                  value={entwuerfe[p.id] ?? ''}
                                  onChange={(e) =>
                                    setEntwuerfe({ ...entwuerfe, [p.id]: e.target.value })
                                  }
                                >
                                  <option value="true">ja</option>
                                  <option value="false">nein</option>
                                </select>
                              ) : (
                                <input
                                  type={p.typ === 'ZAHL' ? 'number' : 'text'}
                                  value={entwuerfe[p.id] ?? ''}
                                  onChange={(e) =>
                                    setEntwuerfe({ ...entwuerfe, [p.id]: e.target.value })
                                  }
                                />
                              )}
                              {/bcrypt/i.test(`${p.bezeichnung ?? ''} ${p.beschreibung ?? ''}`) && (
                                <HashBilden
                                  onUebernehmen={(hash) => setEntwuerfe({ ...entwuerfe, [p.id]: hash })}
                                />
                              )}
                              {p.standardwert && (
                                <div style={{ color: 'var(--grau-500)', fontSize: '0.76rem', marginTop: 4 }}>
                                  Vorgabe: {p.standardwert}
                                </div>
                              )}
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <button
                                className={geaendert ? 'knopf-primaer' : 'knopf-schlicht'}
                                style={{ padding: '6px 14px' }}
                                disabled={!geaendert}
                                onClick={() => void speichern(p)}
                              >
                                Speichern
                              </button>
                            </td>
                          </tr>
                        </Fragment>
                      )
                    })}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}
    </>
  )
}
